/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, Checkbox, Loader, Text, TextInput } from '@mantine/core';
import { FC, useEffect, useState } from 'react';
import { useListState } from '@mantine/hooks';
import { i18n } from '@lingui/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { IconSearch } from '@tabler/icons';
import { EmailFileDto } from '../Types/MessageT';
import { EntityType, FileS } from '../Service/FileS';
import { fetchDocumentsAndContractsByEvent } from '../Service/restapi/documentService';
import { QuestionnaireS } from '../Service/QuestionnaireS';
import { PlaceholderS } from '../Service/PlaceholderS';
import EmptyText from './EmptyText';
import FilePicker from './FilePicker';
import SkeletonItems from './SkeletonItems';

interface Props {
  initialAttachmnets: EmailFileDto[];
  profileId: number;
  eventId?: string;
  onSave: (attachments: EmailFileDto[]) => Promise<boolean>;
}

// eslint-disable-next-line import/prefer-default-export
export const openAttachmentSelectionModal = (props: Props) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Anhang
      </Text>
    ),
    children: <AttachmentSelectionModal {...props} />,
  });
};

const AttachmentSelectionModal: FC<Props> = ({ initialAttachmnets, onSave, profileId, eventId }) => {
  const [attachments, setAttachments] = useState(initialAttachmnets);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const noChange = initialAttachmnets === attachments;
  const attachmentFileNames = attachments.map((a) => a.fileName);

  const toggle = (attachment: EmailFileDto) => {
    if (attachmentFileNames.includes(attachment.fileName)) {
      setAttachments(attachments.filter((a) => a.fileName !== attachment.fileName));
    } else {
      setAttachments([...attachments, attachment]);
    }
  };

  const onClick = async () => {
    setLoading(true);
    const saved = await onSave(attachments);
    setLoading(false);
    if (saved) {
      closeAllModals();
    }
  };

  return (
    <>
      <TextInput
        icon={<IconSearch size={20} />}
        variant="filled"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        placeholder="Dateien suchen"
      />
      <Text weight="bolder" mb="sm" mt="md">
        Dateien aus Deinem Profil
      </Text>
      <EntitiesList
        entityId={profileId.toString()}
        entityType="profiles"
        attachments={attachmentFileNames}
        query={searchQuery}
        toggle={toggle}
      />
      {eventId && (
        <>
          <Text weight="bolder" mt="xl" mb="sm">
            Dateien aus diesem Event
          </Text>
          <EntitiesList
            entityId={eventId.toString()}
            entityType="events"
            attachments={attachmentFileNames}
            query={searchQuery}
            toggle={toggle}
          />
          <Text weight="bolder" mt="xl" mb="sm">
            Dokumente aus diesem Event
          </Text>
          <EventDocumentEntitiesList
            entityId={eventId.toString()}
            entityType="events"
            attachments={attachmentFileNames}
            query={searchQuery}
            toggle={toggle}
          />
        </>
      )}
      <Center mt="xl">
        <Button
          onClick={onClick}
          disabled={loading || noChange}
          rightIcon={loading && <Loader size="xs" />}
          sx={{ width: 220 }}
        >
          {i18n._('actions.save')}
        </Button>
      </Center>
    </>
  );
};

interface EntityListProps {
  entityId: string;
  entityType: EntityType;
  attachments: string[];
  query: string;
  toggle: (attachment: EmailFileDto) => void;
}

const EventDocumentEntitiesList: FC<EntityListProps> = ({ entityId, entityType, attachments, query, toggle }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<EmailFileDto[]>([]);
  const filteredFiles = files.filter((f) => f.fileName.toLowerCase().includes(query.toLowerCase()));
  const notFound = files.length > 0 && filteredFiles.length === 0;

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      let tmp: any = [];
      const resp = await fetchDocumentsAndContractsByEvent(entityId);
      if (resp.ok) {
        resp.json().then((resJSON) => {
          tmp = resJSON;
        });
      }
      const questionnaires = await QuestionnaireS.getExistingByEventId(Number(entityId));
      if (questionnaires) {
        const tmp2: any = questionnaires.map((f) => ({ ...f, fileSize: 0 }));
        setFiles([...tmp, ...tmp2] as any);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-underscore-dangle
  const _toggle = async (file: EmailFileDto) => {
    if (attachments.includes(file.fileName)) {
      toggle(file);
    } else {
      // Check whenever all placeholders are resolved when attaching new bookitup document
      const resolved = await PlaceholderS.getUnresolvedPlaceholders(file.entityId, file.entityType);
      if (resolved) {
        toggle(file);
      }
    }
  };

  return (
    <>
      {loading && <SkeletonItems />}
      {!loading && files.length === 0 && <EmptyText mt="xs" />}
      {!loading && notFound && (
        <Text italic color="dimmed">
          Keine Dateien gefunden
        </Text>
      )}
      {!loading &&
        filteredFiles.length > 0 &&
        filteredFiles.map((file) => (
          <Checkbox
            key={file.entityId}
            checked={attachments.includes(file.fileName)}
            label={file.fileName}
            onChange={() => _toggle(file)}
          />
        ))}
    </>
  );
};

const EntitiesList: FC<EntityListProps> = ({ entityId, entityType, attachments, query, toggle }) => {
  const [loading, setLoading] = useState(false);
  const [files, { setState: setFiles, append }] = useListState<EmailFileDto>([]);
  const filteredFiles = files.filter((f) => f.fileName.toLowerCase().includes(query.toLowerCase()));
  const notFound = files.length > 0 && filteredFiles.length === 0;

  useEffect(() => {
    setLoading(true);
    FileS.getEntityFiles(entityType, entityId)
      .then((resJSON) => {
        const entities = resJSON.map((file) => ({
          entityId: Number(entityId),
          entityType,
          ...file,
        }));
        setFiles(entities);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading && <SkeletonItems />}
      {!loading && files.length === 0 && <EmptyText mt="xs" />}
      {!loading && notFound && (
        <Text italic color="dimmed">
          Keine Dateien gefunden
        </Text>
      )}
      {!loading &&
        filteredFiles.length > 0 &&
        filteredFiles.map((file) => (
          <Checkbox
            key={file.entityId}
            checked={attachments.includes(file.fileName)}
            label={file.fileName}
            onChange={() => toggle(file)}
          />
        ))}
      {entityType === 'profiles' && (
        <FilePicker
          id={entityId}
          onSelectFile={(file) => {
            FileS.put(file, entityType, entityId).then((uploaded) => {
              if (uploaded) {
                const newFile = { entityId: Number(entityId), entityType, fileName: file.name, fileSize: file.size };
                append(newFile);
                toggle(newFile);
              }
            });
          }}
          label={
            <Text mt="md" color="blue" variant="link" sx={{ cursor: 'pointer' }}>
              + Datei hochladen
            </Text>
          }
        />
      )}
    </>
  );
};
